import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import { getAllBlocksByProject } from '../../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { numberToWords } from '../../utils/numberToWords';

// Validation schema
const validationSchema = yup.object().shape({
  absolute: yup.number().required('Final Price is required'),
  gst_percent: yup.string().required('GST % is required'),
  lead_id: yup.number().required('Lead is required'),
  unit_id: yup.number().required('Unit is required'),
  expected_closing_date: yup.string().required('Expected closure date is required')
});

export const UnderConsiderationCreate = ({
  onClose,
  onSubmit,
  isSubmitting,
  selectedLead,
  ucDetails,
  skipFiltering = false
}) => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [unitData, setUnitData] = useState();
  const [priceInWords, setPriceInWords] = useState('');
  // Optional extra charge IDs that are selected (mandatory ones are always included and disabled)
  const [selectedOptionalExtraIds, setSelectedOptionalExtraIds] = useState(new Set());

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      absolute: '',
      gst_percent: '',
      lead_id: Number(selectedLead),
      project: ucDetails?.project,
      block: ucDetails?.block,
      level: ucDetails?.level,
      unit_id: ucDetails?.unit,
      expected_closing_date: ''
    }
  });

  const projectID = watch('project');
  // Track page view
  useEffect(() => {
    mixpanel.track('Under Consideration Create Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  const { data: AllBlocks, refetch: fetchBlocks } = useQuery({
    queryKey: ['Allblocks'],
    queryFn: () => getAllBlocksByProject(projectID)
  });

  React.useEffect(() => {
    fetchBlocks();
  }, [projectID]);

  const transformedDataBlocks =
    AllBlocks?.map((item) => ({
      label: item.name || '',
      id: item.id,
      value: item.id,
      level: item.levels || []
    })).sort((a, b) => a.label.localeCompare(b.label)) || [];

  const [levels, setLevels] = useState([]);
  const BlockChange = (levels) => {
    const formattedLevels = levels
      ?.map((level) => ({
        label: level.name || '',
        id: level.id,
        value: level.id,
        units: level.units || []
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    setLevels(formattedLevels);
  };

  const [units, setUnits] = useState([]);
  const LevelChange = (units) => {
    const availableUnits = skipFiltering
      ? units
      : (units || []).filter(
          (unit) => unit.status === 'VACANT' || unit.status === 'vacant' || unit.status === 'Vacant' //only units with status Available or Vacant
        );
    const formattedUnits = availableUnits
      ?.map((unit) => ({
        label: unit.unitName || '',
        id: unit.id,
        value: unit.id,
        data: unit || {}
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    setUnits(formattedUnits);
  };

  // Set lead_id in form state when selectedLead changes (id or numeric)
  React.useEffect(() => {
    const leadId =
      typeof selectedLead === 'object' && selectedLead != null
        ? selectedLead.id
        : selectedLead;
    if (leadId) {
      setValue('lead_id', Number(leadId));
    }
  }, [selectedLead, setValue]);

  // When unit (and its extra_charges_arr) changes, default all optional extra charges as selected
  React.useEffect(() => {
    const arr = unitData?.extra_charges_arr;
    if (!Array.isArray(arr)) {
      setSelectedOptionalExtraIds(new Set());
      return;
    }
    const optionalIds = arr
      .filter((ec) => ec.isMandatory !== true && ec.isMandatory !== 'true')
      .map((ec) => ec.id);
    setSelectedOptionalExtraIds(new Set(optionalIds));
  }, [unitData?.id, unitData?.extra_charges_arr]);

  // Watch for changes in the absolute (Final Price) field and convert to words
  const finalPrice = watch('absolute');
  React.useEffect(() => {
    if (finalPrice && Number(finalPrice) > 0) {
      setPriceInWords(numberToWords(Number(finalPrice)));
    } else {
      setPriceInWords('');
    }
  }, [finalPrice]);

  // Prefill form fields when ucDetails is available - single comprehensive effect
  React.useEffect(() => {
    if (!ucDetails || !projects || !AllBlocks) return;

    const prefillForm = async () => {
      try {
        // Step 1: Set project
        if (ucDetails.project) {
          const projectExists = projects.some((project) => project.id === ucDetails.project);
          if (projectExists) {
            setValue('project', ucDetails.project);
            // Wait for project to be set and blocks to load
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }

        // Step 2: Set block and load levels
        if (ucDetails.block && AllBlocks.length > 0) {
          const selectedBlock = AllBlocks.find((block) => block.id === ucDetails.block);
          if (selectedBlock && selectedBlock.levels) {
            BlockChange(selectedBlock.levels);
            setValue('block', ucDetails.block);
            // Wait for levels to be loaded
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }

        // Step 3: Set level and load units
        if (ucDetails.level && levels.length > 0) {
          const selectedLevel = levels.find((level) => level.id === ucDetails.level);
          if (selectedLevel && selectedLevel.units) {
            LevelChange(selectedLevel.units);
            setValue('level', ucDetails.level);
            // Wait for units to be loaded
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }

        // Step 4: Set unit and unit data
        if (ucDetails.unit && units.length > 0) {
          const selectedUnit = units.find((unit) => unit.id === ucDetails.unit);
          if (selectedUnit && selectedUnit.data) {
            setUnitData(selectedUnit.data);
            setValue('unit', ucDetails.unit);
            setValue('unit_id', ucDetails.unit);
          }
        }
      } catch (error) {
        console.error('Error prefilling form:', error);
      }
    };

    prefillForm();
  }, [ucDetails, projects, AllBlocks, levels, units, setValue]);

  // Handle cascading effect when project changes (for manual selection)
  React.useEffect(() => {
    if (projectID && AllBlocks && projectID !== ucDetails?.project && !skipFiltering) {
      // Reset form when project changes manually (only if not skipFiltering)
      setValue('block', '');
      setValue('level', '');
      setValue('unit', '');
      setLevels([]);
      setUnits([]);
      setUnitData(undefined);
    }
  }, [projectID, AllBlocks, ucDetails?.project, skipFiltering, setValue]);

  const onSubmitForm = (data) => {
    const { absolute, gst_percent, ...rest } = data;
    // included_extra_charge_ids = mandatory extra charge ids + optional ids that are checked
    const extraArr = unitData?.extra_charges_arr ?? [];
    const mandatoryIds = extraArr
      .filter((ec) => ec.isMandatory === true || ec.isMandatory === 'true')
      .map((ec) => ec.id);
    const optionalSelectedIds = Array.from(selectedOptionalExtraIds);
    const included_extra_charge_ids = [...mandatoryIds, ...optionalSelectedIds];

    console.log("onSubmitForm", {
      lead_id: Number(data.lead_id),
      unit_id: Number(data.unit_id),
      total_price: parseFloat(absolute),
      gst_percentage: parseInt(gst_percent),
      expected_closing_date: data.expected_closing_date,
      ...(included_extra_charge_ids.length > 0 && { included_extra_charge_ids })
    });

    onSubmit({
      lead_id: Number(data.lead_id),
      unit_id: Number(data.unit_id),
      total_price: parseFloat(absolute),
      gst_percentage: parseInt(gst_percent),
      expected_closing_date: data.expected_closing_date,
      ...(included_extra_charge_ids.length > 0 && { included_extra_charge_ids })
    });
  };

  React.useEffect(() => {
    console.log(errors);
  }, [errors]);

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
          Under Consideration Details
        </span>
      </div>
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        <div className="grid grid-cols-2 gap-4">
          {/* Project Name */}
          <FormInput
            width={100}
            label={'Project'}
            type={'dropdown'}
            readOnly={skipFiltering}
            register={register}
            register_key={'project'}
            options={[
              { label: 'Select Project', value: '', disabled: true, selected: true },
              ...projectsForDropdown
            ]}
          />

          {/* Block Name */}
          <FormInput
            width={100}
            label={'Block'}
            type={'dropdown'}
            readOnly={skipFiltering}
            register={register}
            register_key={'block'}
            options={[
              { label: 'Select Block', value: '', disabled: true, selected: true },
              ...transformedDataBlocks
            ]}
            allvalues={(value) => {
              BlockChange(value.level);
              setValue('level', '');
              setValue('unit', '');
              setUnits([]);
              setUnitData(undefined);
            }}
          />

          {/* Level */}
          <FormInput
            width={100}
            label={'Level'}
            type={'dropdown'}
            readOnly={skipFiltering || levels?.length === 0}
            register={register}
            register_key={'level'}
            options={[
              { label: 'Select Level', value: '', disabled: true, selected: true },
              ...levels
            ]}
            allvalues={(value) => {
              LevelChange(value.units);
              setValue('unit', '');
              setUnitData(undefined);
            }}
          />

          {/* Unit */}
          <FormInput
            width={100}
            label={'Unit'}
            type={'dropdown'}
            readOnly={skipFiltering || units?.length === 0}
            register={register}
            register_key={'unit'}
            options={[
              { label: 'Select Unit', value: '', disabled: true, selected: true },
              ...units
            ]}
            allvalues={(value) => {
              setUnitData(value.data);
              setValue('unit_id', value.id); // set unit_id in form state
            }}
          />

          {/* Unit Price */}
          <FormInput
            width={100}
            label={'Unit Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'unit_price'}
            valuePre={unitData?.cost ? `₹${parseInt(unitData.cost).toLocaleString('en-IN')}` : ''}
          />

          <FormInput
            width={100}
            label={'GST'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'gst'}
            valuePre={unitData?.gst ? `₹${parseInt(unitData.gst).toLocaleString('en-IN')}` : ''}
          />

          {unitData?.extra_charges !== '0' && (
            <FormInput
              width={100}
              label={'Extra Charges'}
              type={'text'}
              readOnly={true}
              register={register}
              register_key={'extra_charges'}
              valuePre={
                unitData?.extra_charges
                  ? `₹${parseInt(unitData.extra_charges).toLocaleString('en-IN')}`
                  : ''
              }
            />
          )}

          {/* Extra charges: show checkbox per charge; mandatory = disabled + checked, optional = enabled */}
          {Array.isArray(unitData?.extra_charges_arr) && unitData.extra_charges_arr.length > 0 && (
            <div className="col-span-2 flex flex-col gap-2">
              <label className="font-medium">Extra Charges</label>
              <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded border border-gray-200">
                {unitData.extra_charges_arr.map((ec) => {
                  const isMandatory =
                    ec.isMandatory === true || ec.isMandatory === 'true';
                  const isChecked =
                    isMandatory || selectedOptionalExtraIds.has(ec.id);
                  const isDisabled = isMandatory;
                  return (
                    <label
                      key={ec.id}
                      className={`flex items-center gap-2 ${
                        isDisabled ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'
                      }`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isDisabled}
                        onChange={() => {
                          if (isDisabled) return;
                          setSelectedOptionalExtraIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(ec.id)) next.delete(ec.id);
                            else next.add(ec.id);
                            return next;
                          });
                        }}
                        className="rounded border-gray-300"
                      />
                        <span className="text-sm">
                        {ec.name || 'Extra charge'}
                        {ec.total_price != null && (() => {
                          const base = Number(ec.total_price) || 0;
                          const gstPct = Number(ec.gst) || 0;
                          const gstAmount = (base * gstPct) / 100;
                          const totalWithGst = base + gstAmount;
                          return ` — ₹${totalWithGst.toLocaleString('en-IN', { maximumFractionDigits: 0 })} (₹${base.toLocaleString('en-IN')} + ₹${gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })} GST)`;
                        })()}
                        {isMandatory && (
                          <span className="ml-1 text-gray-500">(Required)</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <FormInput
            width={100}
            label={'Plc Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'plc_price'}
            valuePre={
              unitData?.plc_price ? `₹${parseInt(unitData.plc_price).toLocaleString('en-IN')}` : ''
            }
          />

          <FormInput
            width={100}
            label={'Total Price'}
            type={'text'}
            readOnly={true}
            register={register}
            register_key={'cost'}
            valuePre={`₹${(
              (parseInt(unitData?.cost) || 0) +
              (parseInt(unitData?.plc_price) || 0) +
              (parseInt(unitData?.gst) || 0)
            ).toLocaleString('en-IN')}.00`}
          />

          {/* GST % Radio Buttons */}
          <div className="flex flex-col">
            <label className="mb-1 font-medium">GST %</label>
            <div className="flex gap-4">
              <label>
                <input type="radio" value="0" {...register('gst_percent')} />
                0%
              </label>
              <label>
                <input type="radio" value="1" {...register('gst_percent')} />
                1%
              </label>
              <label>
                <input type="radio" value="5" {...register('gst_percent')} />
                5%
              </label>
            </div>
            {errors.gst_percent && (
              <span className="text-red-500 text-xs">{errors.gst_percent.message}</span>
            )}
          </div>

          {/* Discount (absolute) - moved to last */}
          <div className="flex flex-col">
            <FormInput
              width={100}
              label={'Final Price'}
              register={register}
              register_key={'absolute'}
              type={'number'}
              errors={errors.absolute}
              onType={(value) => {
                const numValue = Number(value);
                if (numValue && numValue > 0) {
                  setPriceInWords(numberToWords(numValue));
                } else {
                  setPriceInWords('');
                }
              }}
            />
            {priceInWords && (
              <span className="text-sm text-gray-600 mt-1 italic">{priceInWords}</span>
            )}
          </div>

          {/* Expected Closure Date */}
          <FormInput
            width={100}
            label={'Expected Closure Date'}
            register={register}
            register_key={'expected_closing_date'}
            type={'date'}
            isRequired={true}
            errors={errors.expected_closing_date}
          />

          {/* Hidden fields */}
          <input type="hidden" {...register('lead_id')} />
          <input type="hidden" {...register('unit_id')} />
        </div>

        <div className="flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            Create
          </Button>
        </div>
      </form>
    </>
  );
};
